import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { comics } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <SEOHead
          title="漫画未找到 - 汗汗漫画"
          description="抱歉，您查找的漫画不存在或已被移除。欢迎浏览汗汗漫画的其他精彩内容。"
          keywords="汗汗漫画,漫画,好看的韩国漫画大全"
          canonicalUrl={`/article/${id}`}
        />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16">
            <div className="px-4 md:px-6 py-20 text-center">
              <span className="w-20 h-20 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-5">
                <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
              </span>
              <h1 className="text-xl font-bold font-heading text-foreground-900 mb-3">漫画未找到</h1>
              <p className="text-foreground-500 text-sm mb-6">该漫画可能已被移除或链接无效</p>
              <Link
                to="/"
                className="inline-block px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 汗汗漫画`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.updatedAt || '2026-01-01',
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '汗汗漫画',
    },
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${comic.author} | 汗汗漫画`}
        description={`${comic.title}，${comic.author}作品，${comic.categoryName}漫画。${comic.description.slice(0, 120)}`}
        keywords={`汗汗漫画,${comic.title},${comic.author},${comic.categoryName},${comic.tags.join(',')},好看的韩国漫画大全`}
        canonicalUrl={`/article/${comic.id}`}
        ogType="article"
        ogImage={comic.cover}
        articlePublishedTime={comic.chapters[0]?.updatedAt}
        articleModifiedTime={comic.updatedAt}
      />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="px-4 md:px-6 py-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: comic.categoryName, href: `/category/${comic.category}` },
                  { label: comic.title },
                ]}
              />

              <article className="mt-4">
                <div className="flex flex-col md:flex-row gap-8">
                  {/* Cover */}
                  <div className="flex-shrink-0 w-full md:w-64">
                    <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        title={`${comic.title} - ${comic.author}`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                      {comic.title}
                    </h1>

                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="text-sm text-foreground-600">
                        作者：<strong>{comic.author}</strong>
                      </span>
                      <span className="text-foreground-300">|</span>
                      <Link
                        to={`/category/${comic.category}`}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
                      >
                        {comic.categoryName}
                      </Link>
                      <span className="text-foreground-300">|</span>
                      <span className={`text-sm font-medium ${comic.status === '连载中' ? 'text-accent-600' : 'text-secondary-600'}`}>
                        {comic.status}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 mb-5">
                      <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-sm font-semibold whitespace-nowrap">
                        {comic.rating} 分
                      </span>
                      <span className="text-sm text-foreground-500">
                        共 {comic.chapters.length} 话
                      </span>
                      <span className="text-sm text-foreground-500">
                        {comic.views.toLocaleString()} 次阅读
                      </span>
                      <span className="text-sm text-foreground-500">
                        更新于 {comic.updatedAt}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-5">
                      {comic.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                      {comic.description}
                    </p>

                    <div className="flex flex-wrap gap-3">
                      <button className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                        开始阅读第一话
                      </button>
                      <button className="px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-semibold hover:bg-background-200/70 transition-colors whitespace-nowrap cursor-pointer border border-background-300/60">
                        收藏漫画
                      </button>
                    </div>
                  </div>
                </div>

                {/* Chapter List */}
                <section className="mt-12">
                  <h2 className="text-xl font-bold font-heading text-foreground-900 mb-5">
                    章节列表
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {comic.chapters.map((chapter) => (
                      <a
                        key={chapter.id}
                        href="#"
                        className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-primary-100/60 transition-colors group cursor-pointer"
                        onClick={(e) => e.preventDefault()}
                      >
                        <span className="text-sm text-foreground-800 group-hover:text-primary-700 whitespace-nowrap">
                          {chapter.title}
                        </span>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">
                          {chapter.updatedAt}
                        </span>
                      </a>
                    ))}
                  </div>
                </section>

                {/* Related Comics */}
                {relatedComics.length > 0 && (
                  <section className="mt-12">
                    <h2 className="text-xl font-bold font-heading text-foreground-900 mb-5">
                      相关漫画推荐
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                      {relatedComics.map((rel) => (
                        <Link
                          key={rel.id}
                          to={`/article/${rel.id}`}
                          className="group cursor-pointer"
                          data-product-shop
                        >
                          <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3 bg-background-200">
                            <img
                              src={rel.cover}
                              alt={rel.title}
                              title={rel.title}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                          <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                            {rel.title}
                          </h3>
                          <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">
                            {rel.author} · {rel.rating}分
                          </p>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}
              </article>
            </div>
          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}